/* eslint-disable quotes */
import React, { createRef } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";

import { Settings } from "@folio/stripes/smart-components";
import { IntlConsumer, TitleManager } from "@folio/stripes/core";

import MaterialTypesSettings from "./MaterialTypesSettings";
import LoanTypesSettings from "./LoanTypesSettings";
import ItemNoteTypesSettings from "./ItemNoteTypesSettings";
import FormatsSettings from "./FormatsSettings";
import ResourceTypesSettings from "./ResourceTypesSettings";
import ContributorTypesSettings from "./ContributorTypesSettings";
import ContributorNameTypesSettings from "./ContributorNameTypesSettings";
import InstanceRelationshipTypesSettings from "./InstanceRelationshipTypesSettings";
import URLRelationshipSettings from "./URLRelationshipSettings";
import StatisticalCodeSettings from "./StatisticalCodeSettings";
import InstanceStatusTypesSettings from "./InstanceStatusTypesSettings";
import HoldingsTypeSettings from "./HoldingsTypeSettings";
import ILLPolicy from "./ILLPolicy";
import HoldingsNoteTypesSettings from "./HoldingsNoteTypesSettings";
import HoldingsSourcesSettings from "./HoldingsSourcesSettings";
import CallNumberTypes from "./CallNumberTypes";
import TargetProfiles from "./TargetProfiles";
import HRIDHandlingSettings from "./HRIDHandling/HRIDHandlingSettings";
import StatisticalCodeTypes from "./StatisticalCodeTypes";
import AlternativeTitleTypesSettings from "./AlternativeTitleTypesSettings";
import IdentifierTypesSettings from "./IdentifierTypesSettings";
import ClassificationTypesSettings from "./ClassificationTypesSettings";
import ModesOfIssuanceSettings from "./ModesOfIssuanceSettings";
import InstanceNoteTypesSettings from "./InstanceNoteTypesSettings";
import NatureOfContentTermsSettings from "./NatureOfContentTermsSettings";
import FastAddSettings from "./FastAdd/FastAddSettings";
import InventoryTemplatesSettings from "./InventoryTemplatesSettings";
import InventoryCustomFieldsSettings from "./InventoryCustomFieldsSettings";
import InventoryDefaultFieldsSettings from "./InventoryDefaultFieldsSettings";
import InventoryOtherSettings from "./InventoryOtherSettings";
import BarcodePatternsSettings from "./BarcodePatternsSettings";
import BarcodeGeneralSettings from "./BarcodeGeneralSettings";
import ArabicLanguageSettings from "./ArabicLanguageSettings";
import SearchFeaturesSettings from "./SearchFeaturesSettings";
import ReindexManagement from "./ReindexManagement";

class InventorySettings extends React.Component {
  constructor(props) {
    super(props);

    this.sections = [
      {
        label: <FormattedMessage id="ui-inventory.instances" />,
        pages: [
          {
            route: "alternativeTitleTypes",
            label: <FormattedMessage id="ui-inventory.alternativeTitleTypes" />,
            component: AlternativeTitleTypesSettings,
            perm: this.addPerm("ui-inventory.settings.alternative-title-types"),
          },
          {
            route: "classificationTypes",
            label: (
              <FormattedMessage id="ui-inventory.classificationIdentifierTypes" />
            ),
            component: ClassificationTypesSettings,
            perm: this.addPerm("ui-inventory.settings.classification-types"),
          },
          {
            route: "contributortypes",
            label: <FormattedMessage id="ui-inventory.contributorTypes" />,
            component: ContributorTypesSettings,
            perm: this.addPerm("ui-inventory.settings.contributor-types"),
          },
          {
            route: "contributornametypes",
            label: <FormattedMessage id="ui-inventory.contributorNameTypes" />,
            component: ContributorNameTypesSettings,
            perm: this.addPerm("ui-inventory.settings.list"),
          },
          {
            route: "instancerelationshiptypes",
            label: <FormattedMessage id="ui-inventory.instanceRelationshipTypes" />,
            component: InstanceRelationshipTypesSettings,
            perm: this.addPerm("ui-inventory.settings.list"),
          },
          {
            route: "formats",
            label: <FormattedMessage id="ui-inventory.formats" />,
            component: FormatsSettings,
            perm: this.addPerm("ui-inventory.settings.instance-formats"),
          },
          {
            route: "instanceNoteTypes",
            label: <FormattedMessage id="ui-inventory.instanceNoteTypes" />,
            component: InstanceNoteTypesSettings,
            perm: this.addPerm("ui-inventory.settings.instance-note-types"),
          },
          {
            route: "instanceStatusTypes",
            label: <FormattedMessage id="ui-inventory.instanceStatusTypes" />,
            component: InstanceStatusTypesSettings,
            perm: this.addPerm("ui-inventory.settings.instance-statuses"),
          },
          {
            route: "modesOfIssuance",
            label: <FormattedMessage id="ui-inventory.modesOfIssuance" />,
            component: ModesOfIssuanceSettings,
            perm: this.addPerm("ui-inventory.settings.modes-of-issuance"),
          },
          {
            route: "natureOfContentTerms",
            label: <FormattedMessage id="ui-inventory.natureOfContentTerms" />,
            component: NatureOfContentTermsSettings,
            perm: this.addPerm("ui-inventory.settings.nature-of-content-terms"),
          },
          {
            route: "identifierTypes",
            label: (
              <FormattedMessage id="ui-inventory.resourceIdentifierTypes" />
            ),
            component: IdentifierTypesSettings,
            perm: this.addPerm("ui-inventory.settings.identifier-types"),
          },
          {
            route: "resourcetypes",
            label: <FormattedMessage id="ui-inventory.resourceTypes" />,
            component: ResourceTypesSettings,
            perm: this.addPerm("ui-inventory.settings.instance-types"),
          },
        ],
      },
      {
        label: <FormattedMessage id="ui-inventory.holdings" />,
        pages: [
          {
            route: "holdingsNoteTypes",
            label: <FormattedMessage id="ui-inventory.holdingsNoteTypes" />,
            component: HoldingsNoteTypesSettings,
            perm: this.addPerm("ui-inventory.settings.holdings-note-types"),
          },
          {
            route: "holdingsSources",
            label: <FormattedMessage id="ui-inventory.holdingsSources" />,
            component: HoldingsSourcesSettings,
            perm: this.addPerm("ui-inventory.settings.holdings-sources"),
          },
          {
            route: "holdingsTypes",
            label: <FormattedMessage id="ui-inventory.holdingsTypes" />,
            component: HoldingsTypeSettings,
            perm: this.addPerm("ui-inventory.settings.holdings-types"),
          },
          {
            route: "ILLPolicy",
            label: <FormattedMessage id="ui-inventory.ILLPolicy" />,
            component: ILLPolicy,
            perm: this.addPerm("ui-inventory.settings.ill-policies"),
          },
        ],
      },
      {
        label: <FormattedMessage id="ui-inventory.items" />,
        pages: [
          {
            route: "itemNoteTypes",
            label: <FormattedMessage id="ui-inventory.itemNoteTypes" />,
            component: ItemNoteTypesSettings,
            perm: this.addPerm("ui-inventory.settings.item-note-types"),
          },
          {
            route: "loantypes",
            label: <FormattedMessage id="ui-inventory.loanTypes" />,
            component: LoanTypesSettings,
            perm: this.addPerm("ui-inventory.settings.loantypes"),
          },
          {
            route: "materialtypes",
            label: <FormattedMessage id="ui-inventory.materialTypes" />,
            component: MaterialTypesSettings,
            perm: this.addPerm("ui-inventory.settings.materialtypes"),
          },
        ],
      },
      {
        label: <FormattedMessage id="ui-inventory.instanceHoldingsItem" />,
        pages: [
          {
            route: "fastAdd",
            label: <FormattedMessage id="ui-inventory.fastAdd" />,
            component: FastAddSettings,
            perm: this.addPerm("ui-inventory.settings.fast-add"),
          },
          {
            route: "hridHandling",
            label: <FormattedMessage id="ui-inventory.hridHandling" />,
            component: HRIDHandlingSettings,
            perm: "ui-inventory.settings.hrid-handling",
          },
          {
            route: "statisticalCodeTypes",
            label: <FormattedMessage id="ui-inventory.statisticalCodeTypes" />,
            component: StatisticalCodeTypes,
            perm: this.addPerm("ui-inventory.settings.statistical-code-types"),
          },
          {
            route: "StatisticalCodeSettings",
            label: <FormattedMessage id="ui-inventory.statisticalCodes" />,
            component: StatisticalCodeSettings,
            perm: this.addPerm("ui-inventory.settings.statistical-codes"),
          },
          {
            route: "URLrelationship",
            label: <FormattedMessage id="ui-inventory.URLrelationship" />,
            component: URLRelationshipSettings,
            perm: this.addPerm(
              "ui-inventory.settings.electronic-access-relationships",
            ),
          },
        ],
      },
      {
        label: <FormattedMessage id="ui-inventory.holdingsItems" />,
        pages: [
          {
            route: "callNumberTypes",
            label: <FormattedMessage id="ui-inventory.callNumberTypes" />,
            component: CallNumberTypes,
            perm: this.addPerm("ui-inventory.settings.call-number-types"),
          },
        ],
      },
      // {
      //   label: (
      //     <FormattedMessage id="ui-inventory.searchIndexingConfiguration" />
      //   ),
      //   pages: [
      //     {
      //       route: "arabicLanguageSettings",
      //       label: (
      //         <FormattedMessage id="ui-inventory.arabicLanguageSettings.title" />
      //       ),
      //       component: ArabicLanguageSettings,
      //       perm: this.addPerm(
      //         "ui-plugin-inventory-templates.searchIndexing.view",
      //       ),
      //     },
      //     {
      //       route: "searchFeaturesSettings",
      //       label: (
      //         <FormattedMessage id="ui-inventory.searchFeaturesSettings.title" />
      //       ),
      //       component: SearchFeaturesSettings,
      //       perm: this.addPerm(
      //         "ui-plugin-inventory-templates.searchIndexing.view",
      //       ),
      //     },
      //     {
      //       route: "reindexManagement",
      //       label: (
      //         <FormattedMessage id="ui-inventory.reindexManagement.title" />
      //       ),
      //       component: ReindexManagement,
      //       perm: this.addPerm(
      //         "ui-plugin-inventory-templates.searchIndexing.view",
      //       ),
      //     },
      //   ],
      // },
    ];

    /** kware start edit section -- Adding inventory templates plugin settings */
    if (
      this.props.stripes.hasPerm(
        "ui-plugin-inventory-templates.inventoryTemplates.view",
      )
    ) {
      this.sections.push(
        {
          label: (
            <FormattedMessage
              id="ui-plugin-inventory-templates.settings.section.templates.label"
              defaultMessage="Templates"
            />
          ),
          pages: [
            {
              route: "templates",
              label: (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.templatesList.pane.paneTitle"
                  defaultMessage="Inventory templates"
                />
              ),
              component: InventoryTemplatesSettings,
              perm: "ui-plugin-inventory-templates.inventoryTemplates.view",
            },
            {
              route: "templates-settings",
              label: (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.otherSettings.pane.paneTitle"
                  defaultMessage="Other settings"
                />
              ),
              component: InventoryOtherSettings,
              perm: "ui-plugin-inventory-templates.inventoryTemplates.view",
            },
          ],
        },
        {
          label: (
            <FormattedMessage
              id="ui-plugin-inventory-templates.settings.section.fieldsManagement.label"
              defaultMessage="Fields management"
            />
          ),
          pages: [
            {
              route: "default-fields",
              label: (
                <FormattedMessage id="ui-plugin-inventory-templates.defaultFields.pane.paneTitle" />
              ),
              component: InventoryDefaultFieldsSettings,
              perm: "ui-plugin-inventory-templates.inventoryTemplates.view",
            },
            {
              route: "custom-fields",
              label: (
                <FormattedMessage id="ui-plugin-inventory-templates.customFields.pane.paneTitle" />
              ),
              component: InventoryCustomFieldsSettings,
              perm: "ui-plugin-inventory-templates.inventoryTemplates.view",
            },
          ],
        },
        {
          label: (
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcode.settings.section.label"
              defaultMessage="Barcode settings"
            />
          ),
          pages: [
            {
              route: "barcode/general",
              label: (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodeGeneralSettings.pane.paneTitle"
                  defaultMessage="General"
                />
              ),
              component: BarcodeGeneralSettings,
              perm: "ui-plugin-inventory-templates.inventoryTemplates.view",
            },
            {
              route: "barcode/patterns",
              label: (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcode.patterns.pane.paneTitle"
                  defaultMessage="Barcode patterns"
                />
              ),
              component: BarcodePatternsSettings,
              perm: "ui-plugin-inventory-templates.inventoryTemplates.view",
            },
          ],
        },
      );
    }
    /** kware end edit section */

    if (
      this.props.stripes.hasInterface("copycat-imports") &&
      this.props.stripes.hasInterface("data-import-converter-storage", "1.3")
    ) {
      this.sections.push({
        label: <FormattedMessage id="ui-inventory.integrations" />,
        pages: [
          {
            route: "targetProfiles",
            label: <FormattedMessage id="ui-inventory.targetProfiles" />,
            component: TargetProfiles,
            perm: "ui-inventory.settings.single-record-import",
          },
        ],
      });
    }

    this.paneTitleRef = createRef();
  }

  componentDidMount() {
    if (this.paneTitleRef.current) {
      this.paneTitleRef.current.focus();
    }
  }

  addPerm = (permission) => {
    const { stripes } = this.props;

    return stripes.hasPerm(permission)
      ? permission
      : "ui-inventory.settings.list.view";
  };

  render() {
    return (
      <IntlConsumer>
        {(intl) => (
          <TitleManager
            page={intl.formatMessage({
              id: "ui-inventory.settings.inventory.title",
            })}
          >
            <Settings
              {...this.props}
              sections={this.sections}
              paneTitle={<FormattedMessage id="ui-inventory.inventory.label" />}
              paneTitleRef={this.paneTitleRef}
              data-test-inventory-settings
            />
          </TitleManager>
        )}
      </IntlConsumer>
    );
  }
}

InventorySettings.propTypes = {
  stripes: PropTypes.object,
};

export default InventorySettings;
